/**
 * The agent CLIs a machine has (Machine.runtimes, from each adapter's detect()): every
 * known agent is listed, so a missing one reads as not installed rather than vanishing.
 */
import { KNOWN_AGENT_KINDS, agentKindLabel, runtimeOf } from '@orgus/protocol';
import type { AgentKind, AgentRuntime, Machine } from '@orgus/protocol';
import { CircleCheck, CircleHelp, CircleSlash, TriangleAlert } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { useState } from 'react';
import { Pressable, RelativeTime, Tooltip, cn, formatInt } from '@orgus/ui';
import { AgentKindLabel } from './AgentKind.tsx';

export type RuntimeState = 'installed' | 'no-version' | 'missing' | 'unknown';

export function runtimeState(r: AgentRuntime | null, reported: boolean): RuntimeState {
  if (!reported) return 'unknown';
  if (!r || !r.installed) return 'missing';
  return r.version ? 'installed' : 'no-version';
}

const STATE: Record<RuntimeState, { icon: LucideIcon; label: string; className: string }> = {
  installed: { icon: CircleCheck, label: 'Installed', className: 'text-fg-muted' },
  'no-version': { icon: TriangleAlert, label: 'Version unknown', className: 'text-waiting' },
  missing: { icon: CircleSlash, label: 'Not installed', className: 'text-fg-subtle' },
  unknown: { icon: CircleHelp, label: 'Not reported', className: 'text-fg-subtle' },
};

/**
 * Why a missing agent reads as not installed: where its adapter looked. Codex is also found
 * inside OpenAI's VS Code extension; every other agent only on PATH.
 */
export function missingRuntimeNote(kind: AgentKind, machineName: string): string {
  if (kind === 'codex') return `Codex was not found on ${machineName}: not on PATH, and not in OpenAI's VS Code extension.`;
  return `${agentKindLabel(kind)} was not found on PATH on ${machineName}.`;
}

/** Known agents first, then anything else the machine reported. */
function kindsOf(m: Pick<Machine, 'runtimes'>): AgentKind[] {
  const extra = (m.runtimes ?? []).map((r) => r.kind).filter((k) => !(KNOWN_AGENT_KINDS as readonly string[]).includes(k));
  return [...KNOWN_AGENT_KINDS, ...extra];
}

/** How many agent CLIs the machine has installed. */
export function installedRuntimes(m: Pick<Machine, 'runtimes'>): number {
  return (m.runtimes ?? []).filter((r) => r.installed).length;
}

function RuntimeRow({ m, kind, reported }: { m: Pick<Machine, 'name' | 'runtimes'>; kind: AgentKind; reported: boolean }) {
  const r = runtimeOf(m, kind);
  const st = runtimeState(r, reported);
  const { icon: Icon, label, className } = STATE[st];
  const detail = st === 'installed' ? `${label} ${r?.version}` : label;
  return (
    <li data-runtime={kind} data-state={st} className="flex h-row items-center gap-2 border-b border-border-subtle px-3 last:border-b-0">
      <AgentKindLabel kind={kind} className="min-w-0 flex-1 text-ui text-fg" />
      {r?.path && st !== 'missing' && <span className="hidden min-w-0 max-w-44 truncate font-mono text-caption text-fg-subtle sm:block">{r.path}</span>}
      <Tooltip content={r?.error ?? (st === 'missing' ? missingRuntimeNote(kind, m.name) : null)}>
        <span tabIndex={r?.error || st === 'missing' ? 0 : -1} className={cn('inline-flex shrink-0 items-center gap-1 text-caption num', className)}>
          <Icon className="size-3.5" aria-hidden />
          {detail}
        </span>
      </Tooltip>
    </li>
  );
}

/**
 * The machine's agent CLIs: the ones it has first, then the rest folded behind one quiet row
 * ("11 not installed"), so a machine with two agents does not read as a list of absences.
 */
export function RuntimeList({ machine: m }: { machine: Pick<Machine, 'name' | 'runtimes'> }) {
  const reported = !!m.runtimes;
  const checkedAt = m.runtimes?.[0]?.checkedAt;
  const [showMissing, setShowMissing] = useState(false);
  const kinds = kindsOf(m);
  const present = reported ? kinds.filter((k) => runtimeState(runtimeOf(m, k), true) !== 'missing') : kinds;
  const missing = reported ? kinds.filter((k) => runtimeState(runtimeOf(m, k), true) === 'missing') : [];
  return (
    <section aria-label="Agents" className="flex flex-col gap-2">
      <h3 className="flex h-control-sm items-center gap-2 text-caption font-medium text-fg-muted">
        Agents
        <span className="font-normal text-fg-subtle">
          {reported && checkedAt ? <RelativeTime ts={checkedAt} prefix="Checked" /> : 'Not reported yet: update its Orgus agent'}
        </span>
      </h3>
      <ul className="overflow-hidden rounded-card border border-border">
        {present.map((kind) => (
          <RuntimeRow key={kind} m={m} kind={kind} reported={reported} />
        ))}
        {showMissing && missing.map((kind) => <RuntimeRow key={kind} m={m} kind={kind} reported={reported} />)}
        {missing.length > 0 && (
          <li>
            <Pressable aria-expanded={showMissing} onClick={() => setShowMissing((v) => !v)} className="flex h-row items-center px-3 text-caption text-fg-subtle hover:bg-hover hover:text-fg-muted">
              {showMissing ? 'Hide the ones not installed' : `${formatInt(missing.length)} more not installed`}
            </Pressable>
          </li>
        )}
      </ul>
    </section>
  );
}
